<template>
  <v-card outlined class="rounded-lg fill-height">
    <v-card-text class="pa-4">

      <!-- Header -->
      <div class="d-flex align-center mb-4">
        <v-avatar size="40" color="primary" class="mr-3" rounded>
          <v-icon color="white">mdi-bullseye-arrow</v-icon>
        </v-avatar>
        <div>
          <div class="subtitle-1 font-weight-bold">Weekly KPIs</div>
          <div class="caption grey--text">AI-estimated time per KPI</div>
        </div>
        <v-spacer />
        <v-btn
          color="primary"
          depressed
          small
          rounded
          class="white--text text-capitalize font-weight-bold"
          @click="openAiSuggestPanel"
        >
          <v-icon small class="mr-1">mdi-shimmer</v-icon>
          AI Suggest
        </v-btn>
      </div>

      <!-- AI Suggest Panel -->
      <v-expand-transition>
        <div v-if="showAiSuggestPanel" class="mb-4">
          <div class="rounded-lg pa-3 grey lighten-4">
            <div class="caption font-weight-bold mb-3 grey--text text--darken-1" style="letter-spacing:0.08em;">
              AI SUGGESTED KPIS
            </div>
            <div v-if="aiSuggestionsLoading" class="d-flex align-center justify-center py-4">
              <v-progress-circular indeterminate size="20" width="2" color="primary" class="mr-2" />
              <span class="caption grey--text">Generating suggestions...</span>
            </div>
            <div
              v-for="(sug, i) in aiSuggestions"
              :key="i"
              class="d-flex align-start mb-3 pb-3"
              :style="i < aiSuggestions.length - 1 ? 'border-bottom:1px solid #e0e0e0;' : ''"
            >
              <v-icon small color="primary" class="mr-2 mt-1">mdi-shimmer</v-icon>
              <div class="flex-grow-1 mr-2">
                <div class="body-2 font-weight-medium">{{ sug.text }}</div>
                <div class="caption grey--text mt-1" style="font-style:italic;">{{ sug.reason }}</div>
              </div>
              <v-btn
                small
                color="primary"
                depressed
                rounded
                class="white--text text-capitalize flex-shrink-0"
                style="min-width:60px;"
                @click="addFromSuggestion(sug)"
              >
                + Add
              </v-btn>
            </div>
            <div v-if="aiSuggestions.length > 0 && !aiSuggestionsLoading" class="d-flex justify-center mt-1">
              <v-btn
                small
                text
                color="primary"
                class="text-capitalize"
                :loading="aiSuggestionsLoading"
                @click="regenBulkKpiSuggestions"
              >
                <v-icon small class="mr-1">mdi-refresh</v-icon>
                Generate new suggestions
              </v-btn>
            </div>
          </div>
        </div>
      </v-expand-transition>

      <!-- Last Week (Locked) -->
      <div class="caption font-weight-bold mb-2 grey--text text--darken-1" style="letter-spacing:0.08em;">
        LAST WEEK (LOCKED)
      </div>
      <div v-if="lastWeekKpis.length === 0" class="caption mb-3 grey--text" style="font-style:italic;">
        No KPIs from last week yet.
      </div>
      <div
        v-for="kpi in lastWeekKpis"
        :key="'lw-' + kpi.id"
        class="d-flex align-center mb-2 pa-2 rounded grey lighten-4"
      >
        <v-icon small color="grey" class="mr-2">mdi-circle-outline</v-icon>
        <span class="caption flex-grow-1 grey--text">{{ kpi.text }}</span>
        <v-chip x-small color="green lighten-4" class="ml-1 green--text text--darken-3">
          {{ kpi.aiHours }}h AI
        </v-chip>
      </div>

      <!-- This Week -->
      <div class="caption font-weight-bold mt-3 mb-2 grey--text text--darken-1" style="letter-spacing:0.08em;">
        THIS WEEK
      </div>

      <div v-for="kpi in thisWeekKpis" :key="kpi.id">
        <!-- KPI row -->
        <div
          class="d-flex align-center mb-1 pa-2 rounded-lg"
          :style="selectedKpiId === kpi.id
            ? 'background:#e3f2fd;border:1px solid #90caf9;cursor:pointer;'
            : 'background:#f5f5f5;border:1px solid transparent;cursor:pointer;'"
          @click="toggleSuggestion(kpi)"
        >
          <v-icon
            small
            class="mr-2 flex-shrink-0"
            :color="kpi.done ? 'primary' : 'grey'"
            @click.stop="toggleDone(kpi)"
          >
            {{ kpi.done ? 'mdi-check-circle' : 'mdi-circle-outline' }}
          </v-icon>
          <span
            v-if="!kpi.aiReason"
            class="body-2 flex-grow-1"
            :class="kpi.done ? 'grey--text' : ''"
            :style="kpi.done ? 'text-decoration:line-through;' : ''"
          >{{ kpi.text }}</span>
          <div v-else class="flex-grow-1">
            <div
              class="body-2"
              :class="kpi.done ? 'grey--text' : ''"
              :style="kpi.done ? 'text-decoration:line-through;' : ''"
            >{{ kpi.text }}</div>
            <div class="caption primary--text" style="font-style:italic;">{{ kpi.aiReason }}</div>
          </div>
          <v-chip x-small color="amber lighten-4" class="ml-2 amber--text text--darken-3 flex-shrink-0 font-weight-bold">
            +100 pts
          </v-chip>
          <v-chip x-small color="green lighten-4" class="ml-1 green--text text--darken-3 flex-shrink-0">
            {{ kpi.aiHours }}h AI
          </v-chip>
          <v-btn
            x-small
            depressed
            :color="selectedKpiId === kpi.id ? 'primary' : 'blue lighten-4'"
            :class="selectedKpiId === kpi.id ? 'white--text' : 'primary--text'"
            class="ml-2 rounded-lg text-capitalize"
            style="min-width:0;padding:0 8px;"
            :loading="kpiAiLoadingId === kpi.id"
            :disabled="kpiAiLoadingId !== null"
            @click.stop="toggleSuggestion(kpi)"
          >
            <v-icon x-small>mdi-shimmer</v-icon>
          </v-btn>
          <v-btn
            x-small
            depressed
            color="red lighten-4"
            class="ml-1 red--text text--darken-2 rounded-lg"
            style="min-width:0;padding:0 8px;"
            @click.stop="removeKpi(kpi.id)"
          >
            <v-icon x-small>mdi-delete-outline</v-icon>
          </v-btn>
        </div>

        <!-- Inline AI Suggestion for this KPI -->
        <v-expand-transition>
          <div
            v-if="selectedKpiId === kpi.id"
            class="mb-2 mx-1 pa-3 rounded-lg blue lighten-5"
            style="border:1px solid #90caf9;"
          >
            <div class="caption font-weight-bold mb-1 grey--text text--darken-1" style="letter-spacing:0.08em;">
              AI SUGGESTION
            </div>
            <!-- Loading -->
            <div v-if="kpiAiLoadingId === kpi.id" class="d-flex align-center py-1">
              <v-progress-circular indeterminate size="14" width="2" color="primary" class="mr-2" />
              <span class="caption grey--text">Generating suggestion...</span>
            </div>
            <!-- Result -->
            <template v-else-if="kpi.aiSuggestion">
              <div class="body-2 font-weight-medium mb-1">{{ kpi.aiSuggestion.text }}</div>
              <div class="caption grey--text mb-2" style="font-style:italic;">{{ kpi.aiSuggestion.reason }}</div>
              <div class="d-flex align-center" style="gap:8px;">
                <v-btn
                  x-small
                  color="primary"
                  depressed
                  rounded
                  class="white--text text-capitalize"
                  @click.stop="useInlineSuggestion(kpi)"
                >
                  <v-icon x-small class="mr-1">mdi-check</v-icon>
                  Use this
                </v-btn>
                <v-btn
                  x-small
                  depressed
                  rounded
                  color="blue lighten-3"
                  class="white--text text-capitalize"
                  :loading="kpiAiLoadingId === kpi.id"
                  @click.stop="regenKpiSuggestion(kpi)"
                >
                  <v-icon x-small class="mr-1">mdi-refresh</v-icon>
                  Generate new
                </v-btn>
                <v-btn
                  x-small
                  text
                  class="text-capitalize grey--text"
                  @click.stop="selectedKpiId = null"
                >
                  × Dismiss
                </v-btn>
              </div>
            </template>
          </div>
        </v-expand-transition>
      </div>

      <!-- Add KPI input -->
      <div class="d-flex align-center mt-3" style="gap:8px;">
        <v-text-field
          v-model="newKpi"
          placeholder="Add a KPI for this week..."
          dense
          hide-details
          outlined
          class="flex-grow-1 rounded-lg"
          @keyup.enter="addKpi"
        />
        <v-btn
          color="primary"
          icon
          depressed
          :disabled="!newKpi.trim()"
          @click="addKpi"
          style="width:36px;height:36px;"
        >
          <v-icon color="white">mdi-plus</v-icon>
        </v-btn>
      </div>

      <!-- Edit dialog -->
      <v-dialog v-model="editDialog" max-width="400">
        <v-card class="rounded-lg">
          <v-card-title class="subtitle-1">Edit KPI</v-card-title>
          <v-card-text>
            <v-text-field
              v-model="editText"
              dense
              outlined
              hide-details
              label="KPI text"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text class="text-capitalize grey--text" @click="editDialog = false">Cancel</v-btn>
            <v-btn color="primary" depressed small class="white--text text-capitalize rounded-lg" @click="saveEdit">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </v-card-text>
  </v-card>
</template>

<script>
import { askAI, parseAIJson, buildUserContext } from '@/services/growthAiService';

// Shared role-ID → profile map (matches AiCoachCard / HabitTrackerCard / GrowthAreasCard).
const ID_TO_PROFILE = {
  1: 'superadmin', 2: 'management', 3: 'agent',
  4: 'engineering', 5: 'engineering', 9: 'engineering',
  6: 'marketing', 7: 'design', 8: 'sales',
  10: 'recruitment', 11: 'management', 12: 'operations', 13: 'finance',
};
const PROFILE_PRIORITY = {
  superadmin: 100, management: 90, sales: 70, agent: 65, engineering: 60,
  recruitment: 55, marketing: 50, design: 45, operations: 40, finance: 35, generic: 0,
};

export default {
  name: 'KpiTrackerCard',
  inject: {
    personalTasks: { default: () => () => [] },
    assignedTasks: { default: () => () => [] },
    allManagers: { default: () => () => [] },
    roles: { default: () => () => [] },
  },
  data() {
    return {
      loading: false,
      newKpi: '',
      showAiSuggestPanel: false,
      aiSuggestionsLoading: false,
      kpiAiLoadingId: null,
      selectedKpiId: null,
      editDialog: false,
      editText: '',
      editingKpiId: null,
      lastWeekKpis: [],
      thisWeekKpis: [],
      aiSuggestions: [],
      // Loaded so AI can anchor KPIs on long-term direction, not just weekly tasks.
      growthItems: [],
    };
  },
  computed: {
    storeUser() { return this.$store.state.auth?.user || {}; },
    userRoleIds() {
      const rm = Array.isArray(this.storeUser.role_members) ? this.storeUser.role_members : [];
      return rm.map(m => m.role?.id).filter(id => id != null);
    },
    userRoleNames() {
      const rm = Array.isArray(this.storeUser.role_members) ? this.storeUser.role_members : [];
      return rm.map(m => m.role?.name).filter(Boolean);
    },
    managedRoleIds() {
      const uid = this.storeUser.id;
      const managers = typeof this.allManagers === 'function' ? this.allManagers() : [];
      if (!Array.isArray(managers) || !uid) return [];
      return managers
        .filter(m => m.user_id === uid && (m.is_manager === true || m.is_manager === 1 || m.is_manager === undefined))
        .map(m => m.user_role_id)
        .filter(id => id != null);
    },
    managedRoleNames() {
      const rolesList = typeof this.roles === 'function' ? this.roles() : [];
      if (!Array.isArray(rolesList)) return [];
      return this.managedRoleIds
        .map(id => rolesList.find(r => r.id === id)?.name)
        .filter(Boolean);
    },
    roleProfile() {
      const ids = new Set(this.userRoleIds);
      const legacy = this.storeUser.role_id;
      if (legacy === 1) ids.add(1);
      if (legacy === 11) ids.add(11);
      if (legacy === 2) ids.add(2);
      const profiles = new Set();
      ids.forEach(id => { const p = ID_TO_PROFILE[id]; if (p) profiles.add(p); });
      if (this.managedRoleIds.length > 0) profiles.add('management');
      const arr = Array.from(profiles).sort(
        (a, b) => (PROFILE_PRIORITY[b] || 0) - (PROFILE_PRIORITY[a] || 0)
      );
      return arr[0] || 'generic';
    },
    primaryRoleName() {
      return this.userRoleNames[0] || (this.storeUser.role_id === 1 || this.storeUser.role_id === 11
        ? 'Admin' : this.storeUser.role_id === 2 ? 'Manager' : 'Employee');
    },
    allTasks() {
      const p = typeof this.personalTasks === 'function' ? this.personalTasks() : [];
      const a = typeof this.assignedTasks === 'function' ? this.assignedTasks() : [];
      return [...p, ...a].filter(t => !t.deleted_at);
    },
    todayStr() { return new Date().toISOString().slice(0, 10); },
    overdueTasks() {
      const today = this.todayStr;
      return this.allTasks.filter(t => !t.completed_at && t.due_date && String(t.due_date).slice(0, 10) < today);
    },
    inProgressTasks() {
      return this.allTasks.filter(t => !t.completed_at && (t.status?.name || '').toLowerCase().includes('progress'));
    },
    dueThisWeek() {
      const today = new Date();
      const weekOut = new Date(Date.now() + 7 * 24 * 3600 * 1000).toISOString().slice(0, 10);
      const t = today.toISOString().slice(0, 10);
      return this.allTasks.filter(x => !x.completed_at && x.due_date
        && String(x.due_date).slice(0, 10) >= t
        && String(x.due_date).slice(0, 10) <= weekOut);
    },
    completedThisWeek() {
      const weekAgo = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString().slice(0, 10);
      return this.allTasks.filter(t => t.completed_at && String(t.completed_at).slice(0, 10) >= weekAgo);
    },
    lastWeekDoneCount() { return this.lastWeekKpis.filter(k => k.done).length; },
    lastWeekMissedCount() { return this.lastWeekKpis.filter(k => !k.done).length; },
  },
  mounted() {
    this.load();
    this.loadSideContext();
    // Sync with global growth bus so KPI list + side context refreshes when
    // other cards mutate data (e.g. task completion, growth area toggles).
    this.$root.$on('growth:refresh', this.onGrowthRefresh);
  },
  beforeDestroy() {
    this.$root.$off('growth:refresh', this.onGrowthRefresh);
  },
  methods: {
    onGrowthRefresh() {
      this.load();
      this.loadSideContext();
    },
    normalizeKpi(k) {
      return { id: k.id, text: k.text, aiReason: k.ai_reason || null, aiHours: k.ai_hours, done: !!k.is_done, aiSuggestion: null };
    },
    async load() {
      this.loading = true;
      try {
        const res = await this.$axios.get('api/employee-growth/kpis');
        if (res.data.response) {
          this.thisWeekKpis = (res.data.data.this_week || []).map(this.normalizeKpi);
          this.lastWeekKpis = (res.data.data.last_week || []).map(this.normalizeKpi);
        }
      } finally {
        this.loading = false;
      }
    },
    // Pull long-term growth areas so KPIs can roll up to real direction,
    // not just match this week's inbox.
    async loadSideContext() {
      try {
        const res = await this.$axios.get('api/employee-growth/areas');
        if (res.data?.response) {
          this.growthItems = (res.data.data || []).map(a => ({
            text: a.text, done: !!a.is_done, source: a.source,
          }));
        }
      } catch (e) { /* non-critical */ }
    },
    // Evidence block for KPI generation. KPIs are the metrics — so the signals
    // are: what real work the user is doing, what tasks are at risk, how they
    // did last week, what longer-term growth they're pursuing, and role lanes.
    buildKpiSignalBlock() {
      const profile = this.roleProfile;
      const signals = [];

      // Last-week performance → calibrate ambition
      if (this.lastWeekKpis.length > 0) {
        const ratio = this.lastWeekDoneCount / this.lastWeekKpis.length;
        if (ratio >= 0.8) {
          signals.push(`- LAST WEEK: hit ${this.lastWeekDoneCount}/${this.lastWeekKpis.length} KPIs → raise the bar, suggest stretch KPIs with higher targets.`);
        } else if (ratio <= 0.4) {
          signals.push(`- LAST WEEK: only ${this.lastWeekDoneCount}/${this.lastWeekKpis.length} KPIs hit → suggest smaller, more winnable KPIs (cut target by ~30%).`);
        } else {
          signals.push(`- LAST WEEK: ${this.lastWeekDoneCount}/${this.lastWeekKpis.length} KPIs hit → keep ambition similar, fix what slipped.`);
        }
        const missed = this.lastWeekKpis.filter(k => !k.done).slice(0, 3).map(k => `"${k.text}"`);
        if (missed.length) signals.push(`- MISSED LAST WEEK: ${missed.join(', ')} → consider re-running or replacing with a clearer version.`);
      } else {
        signals.push(`- LAST WEEK: no KPIs tracked yet → start with 3-4 foundational KPIs that establish a baseline.`);
      }

      // Current workload signals → KPIs must fit the week's reality
      if (this.dueThisWeek.length > 0) {
        const sample = this.dueThisWeek.slice(0, 6).map(t => `"${t.name}"`).join(', ');
        signals.push(`- TASKS DUE THIS WEEK (${this.dueThisWeek.length}): ${sample}${this.dueThisWeek.length > 6 ? ', …' : ''} → at least 1-2 KPIs should directly measure completion/quality of these.`);
      }
      if (this.overdueTasks.length >= 3) {
        signals.push(`- OVERDUE (${this.overdueTasks.length}): recovery KPI needed — e.g. "clear X overdue items by Friday".`);
      }
      if (this.inProgressTasks.length >= 4) {
        signals.push(`- WIP OVERLOAD (${this.inProgressTasks.length} in progress): suggest a throughput KPI like "ship X WIP items this week" over starting new work.`);
      }
      if (this.completedThisWeek.length >= 5 && this.overdueTasks.length === 0) {
        signals.push(`- HIGH EXECUTION (${this.completedThisWeek.length} done, 0 overdue): add quality / outcome / leadership KPIs, not more volume.`);
      }
      if (this.allTasks.length === 0) {
        signals.push(`- NO TASKS TRACKED: lean on ROLE LANES to pick KPIs.`);
      }

      // Actual work sample → anchor on real domain, not generic metrics
      const sample = this.allTasks.slice(0, 20).map(t => t.name || '').filter(Boolean);
      if (sample.length > 0) {
        signals.push(`- WORK SAMPLE: ${sample.slice(0, 6).map(n => `"${n}"`).join(', ')}${sample.length > 6 ? ', …' : ''} → KPIs should measure outputs of THIS kind of work.`);
      }

      // Growth direction → KPIs should ladder up to longer-term goals
      const activeGrowth = this.growthItems.filter(g => !g.done).slice(0, 4);
      if (activeGrowth.length > 0) {
        signals.push(`- LONG-TERM GROWTH AREAS: ${activeGrowth.map(g => `"${g.text}"`).join(', ')} → at least 1 KPI should be a weekly step toward one of these.`);
      }

      // Leadership
      if (this.managedRoleNames.length > 0) {
        signals.push(`- MANAGES: ${this.managedRoleNames.join(', ')} team(s) → include at least 1 leadership/coaching KPI (1-on-1s held, blockers removed, feedback given, team metric moved).`);
      }

      // Role lanes — what "good" KPIs look like per profile
      const kpiLanes = {
        superadmin: 'org outcomes: revenue/retention moved, strategic decisions shipped, org-wide unblocks, leadership 1-on-1s done',
        management: 'team outcomes: 1-on-1s held, feedback delivered, team OKR progress, hiring loops closed, blockers cleared',
        sales: 'pipeline outcomes: # discovery calls, # qualified opps, pipeline $ added, close rate, CRM hygiene score, proposals sent',
        agent: 'client outcomes: tickets resolved, first-response time, CSAT, escalations handled, knowledge-base articles authored',
        engineering: 'shipping outcomes: PRs merged, reviews given, bugs closed, incidents resolved, docs updated, test coverage delta',
        marketing: 'campaign outcomes: assets shipped, experiments run, leads generated, CTR/conversion delta, attribution reviewed',
        design: 'design outcomes: designs shipped, reviews given, research sessions run, components added to system, stakeholder approvals',
        recruitment: 'pipeline outcomes: candidates sourced, screens done, offers out, offers accepted, time-to-hire trend',
        operations: 'ops outcomes: processes documented, SLA adherence %, vendor issues resolved, automations shipped, cost delta',
        finance: 'finance outcomes: forecasts delivered on time, variance vs actual, close days, reports shipped, controls tested',
        generic: 'universal: outputs shipped, blockers resolved, skills practiced, stakeholder commitments kept',
      };
      signals.push(`- ROLE LANES (${profile}): ${kpiLanes[profile] || kpiLanes.generic}.`);

      return signals.join('\n');
    },
    async addKpi() {
      const t = this.newKpi.trim();
      if (!t) return;
      try {
        const res = await this.$axios.post('api/employee-growth/kpis', { text: t });
        if (res.data.response) {
          this.thisWeekKpis.push(this.normalizeKpi(res.data.data));
          this.newKpi = '';
        }
      } catch (e) { this.$toast && this.$toast.error('Failed to add KPI'); }
    },
    async removeKpi(id) {
      try {
        await this.$axios.delete(`api/employee-growth/kpis/${id}`);
        this.thisWeekKpis = this.thisWeekKpis.filter(k => k.id !== id);
        if (this.selectedKpiId === id) this.selectedKpiId = null;
      } catch (e) { this.$toast && this.$toast.error('Failed to delete KPI'); }
    },
    async regenKpiSuggestion(kpi) {
      this.$set(kpi, 'aiSuggestion', null);
      await this.toggleSuggestion(kpi);
    },
    async regenBulkKpiSuggestions() {
      if (this.aiSuggestionsLoading) return;
      this.aiSuggestions = [];
      this.aiSuggestionsLoading = true;
      try {
        const existing = this.thisWeekKpis.map(k => k.text).join('; ') || 'none';
        const userCtx = buildUserContext(this, { kpis: this.thisWeekKpis, growthAreas: this.growthItems });
        const signals = this.buildKpiSignalBlock();
        const system = `You are a performance coach for a ${this.primaryRoleName} (role profile: ${this.roleProfile}).
Generate exactly 4 weekly KPI suggestions grounded in the SIGNALS below.
Return ONLY a JSON array with exactly 4 objects: [{"text": "...", "reason": "..."}]
SIGNALS:
${signals}
USER CONTEXT:
${userCtx}`;
        const raw = await askAI(
          `Existing KPIs this week: ${existing}. Suggest 4 new weekly KPIs grounded in my real tasks and last-week performance.`,
          system,
          { logSession: false, isHighImpact: false },
        );
        this.aiSuggestions = parseAIJson(raw);
      } catch (e) {
        this.$toast && this.$toast.error('Failed to refresh suggestions');
      } finally {
        this.aiSuggestionsLoading = false;
      }
    },
    async toggleSuggestion(kpi) {
      // If already open, close it
      if (this.selectedKpiId === kpi.id) {
        this.selectedKpiId = null;
        return;
      }
      this.selectedKpiId = kpi.id;
      // If suggestion already loaded, just show it
      if (kpi.aiSuggestion) return;
      // Fetch AI suggestion for this specific KPI
      this.kpiAiLoadingId = kpi.id;
      try {
        const userCtx = buildUserContext(this, { kpis: this.thisWeekKpis, growthAreas: this.growthItems });
        const signals = this.buildKpiSignalBlock();
        const system = `You are a performance coach for a ${this.primaryRoleName} (role profile: ${this.roleProfile}).
Upgrade the user's weekly KPI into one that is SMART (specific, measurable, achievable, relevant, time-bound) AND anchored on the evidence below.

RULES:
- Keep the user's intent — do not switch topic.
- Add a number (count, %, $, hours) and a deadline within this week.
- If possible, tie the KPI to a specific task they already have due this week, a missed KPI from last week, or a growth area.
- If last-week hit rate was low, make the number easier (~30% lower); if high, push it higher.
- Max 20 words for the KPI.
- "reason" must cite the SIGNAL it uses (e.g. "measures your 4 tasks due Fri", "re-runs last week's missed pipeline KPI"). Max 15 words.
- Do NOT paraphrase — UPGRADE with real evidence.

Return ONLY valid JSON: {"text": "improved KPI (max 20 words)", "reason": "why this upgrade beats the original (max 15 words, cite a signal)"}

SIGNALS:
${signals}

USER CONTEXT:
${userCtx}`;
        // Log as high-impact AI session → awards AI-pillar points (spec: 5 pts, cap 20/day).
        const raw = await askAI(
          `Upgrade this weekly KPI for me: "${kpi.text}"`,
          system,
          { logSession: true, isHighImpact: true },
        );
        kpi.aiSuggestion = parseAIJson(raw);
      } catch (e) {
        this.$toast && this.$toast.error('Failed to get AI suggestion');
        this.selectedKpiId = null;
      } finally {
        this.kpiAiLoadingId = null;
      }
    },
    async useInlineSuggestion(kpi) {
      const newText = kpi.aiSuggestion.text;
      const newReason = kpi.aiSuggestion.reason;
      kpi.text = newText;
      kpi.aiReason = newReason;
      kpi.aiSuggestion = null;
      this.selectedKpiId = null;
      try {
        await this.$axios.post(`api/employee-growth/kpis/${kpi.id}`, { text: newText, ai_reason: newReason });
      } catch (e) {
        this.$toast && this.$toast.error('Failed to save updated KPI');
      }
    },
    async addFromSuggestion(sug) {
      try {
        const res = await this.$axios.post('api/employee-growth/kpis', { text: sug.text, ai_reason: sug.reason });
        if (res.data.response) {
          this.thisWeekKpis.push(this.normalizeKpi(res.data.data));
          this.showAiSuggestPanel = false;
        }
      } catch (e) { this.$toast && this.$toast.error('Failed to add KPI'); }
    },
    async openAiSuggestPanel() {
      this.showAiSuggestPanel = !this.showAiSuggestPanel;
      if (this.showAiSuggestPanel && !this.aiSuggestionsLoading) {
        this.aiSuggestions = [];
        this.aiSuggestionsLoading = true;
        try {
          const existing = this.thisWeekKpis.map(k => k.text).join('; ') || 'none';
          const userCtx = buildUserContext(this, { kpis: this.thisWeekKpis, growthAreas: this.growthItems });
          const signals = this.buildKpiSignalBlock();
          const system = `You are a performance coach for a ${this.primaryRoleName} (role profile: ${this.roleProfile}).
Generate exactly 4 weekly KPI suggestions. Every KPI MUST be grounded in the SIGNALS below — not generic advice.

MANDATORY COMPOSITION (fill all 4 slots from these buckets, prioritized by available signals):
1. TASK-LINKED KPI: if there are tasks due this week, at least 1 KPI must measure completion/quality of them.
2. RECOVERY KPI: if overdue > 2 or a KPI was missed last week, at least 1 KPI must address it.
3. LEADERSHIP KPI: if user manages a team, at least 1 KPI must be team-focused (1-on-1s, feedback, blockers removed, team metric).
4. GROWTH-LADDER KPI: at least 1 KPI should be a weekly step toward an active long-term growth area (if any exist).
5. Any remaining slots: pick from ROLE LANES, matched to their actual work sample.

RULES PER KPI:
- Format: "[verb] [number] [specific noun] by [deadline this week]" — e.g. "Hold 3 discovery calls by Thursday EOD".
- Include a concrete number (count, %, $, hours).
- Include a deadline within this week (Mon-Fri or "by Friday", "this week").
- Max 18 words.
- Must be distinct from existing KPIs: ${existing}
- Calibrate ambition from last week's hit rate (see SIGNALS).
- "reason" max 14 words, MUST cite the specific signal used (e.g. "covers 2 overdue client tasks", "re-runs last week's missed forecast KPI", "steps toward 'improve discovery' growth area").
- No vague KPIs. No "improve X" or "be better at Y" — always measurable.

Return ONLY a JSON array with exactly 4 objects: [{"text": "...", "reason": "..."}]

SIGNALS:
${signals}

USER CONTEXT:
${userCtx}`;
          // Log as high-impact AI session → awards AI-pillar points (spec: 5 pts, cap 20/day).
          const raw = await askAI(
            `Existing KPIs this week: ${existing}. Suggest 4 new weekly KPIs grounded in my real tasks and last-week performance.`,
            system,
            { logSession: true, isHighImpact: true },
          );
          this.aiSuggestions = parseAIJson(raw);
        } catch (e) {
          this.$toast && this.$toast.error('Failed to load AI suggestions');
        } finally {
          this.aiSuggestionsLoading = false;
        }
      }
    },
    startEdit(kpi) {
      this.editingKpiId = kpi.id;
      this.editText = kpi.text;
      this.editDialog = true;
    },
    async saveEdit() {
      const kpi = this.thisWeekKpis.find(k => k.id === this.editingKpiId);
      if (!kpi || !this.editText.trim()) { this.editDialog = false; return; }
      try {
        const res = await this.$axios.post(`api/employee-growth/kpis/${this.editingKpiId}`, { text: this.editText.trim() });
        if (res.data.response) kpi.text = this.editText.trim();
      } catch (e) { this.$toast && this.$toast.error('Failed to save'); }
      this.editDialog = false;
    },
    async toggleDone(kpi) {
      try {
        const res = await this.$axios.post(`api/employee-growth/kpis/${kpi.id}`, { is_done: !kpi.done });
        if (res.data.response) {
          kpi.done = !kpi.done;
          this.$root.$emit('growth:refresh');
        }
      } catch (e) { this.$toast && this.$toast.error('Failed to update'); }
    },
  },
};
</script>


